import { combineReducers, type Reducer } from 'redux';
import { createSelector } from 'reselect';
import { pathOr } from 'ramda';
import { SET_COMMON_CONFIGURATION } from '../../constants/configuration';
import { formatAPIUrl } from '../../../utils/templater';
import type { Nullable } from '../../../types';
import type { RootState } from '../../store';

const isDevelopmentMode = process.env.NODE_ENV === 'development';
const DEFAULT_HOSTNAME = process.env.REACT_APP_DEFAULT_HOSTNAME;

export interface ConfigurationState {
  imageTemplates: {
    CATEGORY_LOGO: string;
    CATEGORY_ICON: string;
    CATEGORY_BACKGROUND: string;
    PROVIDER_LOGO_MINI: string;
    GAME_IMAGE: string;
    PROVIDER_LOGO: string;
    GAME_COMMON_BACKGROUND: string;
    NAVIGATION_LOGO: string;
    NAVIGATION_LOGO_WITHOUT_FILE_EXTENSION: string;
  };
  optimizeContainerId: Nullable<string>;
  contentAPIPrefix: string;
  randomSlotCategoryId: Nullable<string>;
  randomSlotId: Nullable<string>;
  randomCategories: Array<string>;
  randomGamePosition: number;
  aggressiveIframeLaunchMode: boolean;
  useAMSSegments: boolean;
  ASMSegmentsRefreshHours: number;
  centrifugoURL: string;
  deeplinkProtocol: string;
  themeSettings: {
    theme: string;
    additionalServiceClassName: string;
    mode: string;
    unmountMode: string;
  };
  sidebarMenuItemLimits: {
    categories: number;
    providers: number;
  };
  operatorId: string;
  frontAPIPrefix: string;
  promoBonusSystemApiPrefix?: string;
  unifiedSidebar: boolean;
}

export const initialState: ConfigurationState = {
  imageTemplates: {
    CATEGORY_LOGO: '',
    CATEGORY_ICON: '',
    CATEGORY_BACKGROUND: '',
    PROVIDER_LOGO: '',
    PROVIDER_LOGO_MINI: '', // newest
    GAME_IMAGE: '',
    GAME_COMMON_BACKGROUND: '',
    NAVIGATION_LOGO: '',
    NAVIGATION_LOGO_WITHOUT_FILE_EXTENSION: '',
  },
  sidebarMenuItemLimits: {
    categories: 5,
    providers: 5,
  },
  optimizeContainerId: null,
  contentAPIPrefix: '',
  randomSlotCategoryId: null,
  randomSlotId: null,
  randomCategories: [],
  randomGamePosition: 16,
  aggressiveIframeLaunchMode: false,
  useAMSSegments: false,
  ASMSegmentsRefreshHours: 4,
  centrifugoURL: '',
  deeplinkProtocol: 'nomadcasino://',
  themeSettings: {
    theme: '',
    additionalServiceClassName: '',
    mode: '',
    unmountMode: '',
  },
  operatorId: '',
  frontAPIPrefix: '',
  promoBonusSystemApiPrefix: '',
  unifiedSidebar: false,
};

export function commonReducer(
  state = initialState,
  { payload, type }: { type: string; payload: ConfigurationState }
): ConfigurationState {
  return type === SET_COMMON_CONFIGURATION
    ? { ...initialState, ...payload }
    : state;
}

export default combineReducers<{
  common: Reducer<ConfigurationState, any, any>;
}>({
  common: commonReducer,
});

export const commonConfigurationSelector = (state: RootState) =>
  pathOr(initialState, ['app', 'configuration', 'common'], state);

export const contentAPIPrefixSelector = createSelector(
  commonConfigurationSelector,
  ({ contentAPIPrefix }) =>
    formatAPIUrl(contentAPIPrefix || initialState.contentAPIPrefix)
);

export const isAMSEnabledSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.useAMSSegments, ['useAMSSegments'])
);

export const AMSRefreshHoursSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.ASMSegmentsRefreshHours, ['ASMSegmentsRefreshHours'])
);

export const randomGameCategoryIdSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.randomSlotCategoryId, ['randomSlotCategoryId'])
);

export const randomGameIdSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.randomSlotId, ['randomSlotId'])
);

export const randomGamePositionSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.randomGamePosition, ['randomGamePosition'])
);

export const randomCategoriesSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.randomCategories, ['randomCategories'])
);

export const imageTemplatesSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.imageTemplates, ['imageTemplates'])
);

export const gameImageSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.GAME_IMAGE, ['GAME_IMAGE'])
);

export const gameCommonBackgroundSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.GAME_COMMON_BACKGROUND, [
    'GAME_COMMON_BACKGROUND',
  ])
);

export const categoryLogoSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.CATEGORY_LOGO, ['CATEGORY_LOGO'])
);

export const categoryIconSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.CATEGORY_ICON, ['CATEGORY_ICON'])
);

export const providerLogoImagePathSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.PROVIDER_LOGO, ['PROVIDER_LOGO'])
);

export const providerColouredLogoSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.PROVIDER_LOGO_MINI, ['PROVIDER_LOGO_MINI'])
);

export const navigationLogoImagePathSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.NAVIGATION_LOGO, ['NAVIGATION_LOGO'])
);

export const navigationLogoImagePathWithoutExtensionSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.NAVIGATION_LOGO_WITHOUT_FILE_EXTENSION, [
    'NAVIGATION_LOGO_WITHOUT_FILE_EXTENSION',
  ])
);

export const sidebarMenuItemLimitsSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.sidebarMenuItemLimits, ['sidebarMenuItemLimits'])
);

export const aggressiveIframeLaunchModeSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.aggressiveIframeLaunchMode, [
    'aggressiveIframeLaunchMode',
  ])
);

export const centrifugoURLSelector = createSelector(
  commonConfigurationSelector,
  (commonConfig) => {
    const centrifugoURL =
      commonConfig?.centrifugoURL ?? initialState?.centrifugoURL;
    const hostname =
      isDevelopmentMode && DEFAULT_HOSTNAME
        ? DEFAULT_HOSTNAME
        : window.location.host;

    return formatAPIUrl(centrifugoURL, {
      ':hostname': hostname,
    });
  }
);

export const themeSettingsSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.themeSettings, ['themeSettings'])
);

export const deeplinkProtocolSelector = createSelector(
  commonConfigurationSelector,
  (state) => state?.deeplinkProtocol || initialState.deeplinkProtocol
);

export const operatorIdSelector = createSelector(
  commonConfigurationSelector,
  (state) => state?.operatorId || initialState.operatorId
);
